/* Spotlight step (DESIGN-tutorial-system.md §1.1): dims the page, cuts a hole
 * around the anchored element (box-shadow trick — the dim never intercepts a
 * click), and floats a card with title, copy and Next/Back/Skip. With no
 * resolvable anchor the card centers over a plain dim. The anchor is re-queried
 * on an interval so late-mounting elements (tab switches, conditional panels)
 * get picked up without any observer plumbing. */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

export interface Rect { top: number; left: number; width: number; height: number }

const near = (a: number, b: number) => Math.abs(a - b) < 0.5;
const sameRect = (a: Rect | null, b: Rect | null) =>
  (!a && !b) ||
  (!!a && !!b && near(a.top, b.top) && near(a.left, b.left) && near(a.width, b.width) && near(a.height, b.height));

/** Visible element carrying the data-tut anchor. When an anchor exists in more
 *  than one place (e.g. `design-segment` lives on both the "New model" button
 *  and the segment grid inside the modal it opens), the LAST match in DOM order
 *  wins — modals/overlays render after their trigger, so the highlight follows
 *  the user into the surface the step is actually about. */
function findAnchor(anchor: string): Element | null {
  const els = document.querySelectorAll(`[data-tut="${anchor}"]`);
  let found: Element | null = null;
  for (const el of els) {
    const r = el.getBoundingClientRect();
    if (r.width > 0 && r.height > 0) found = el;
  }
  return found;
}

export function Spotlight({
  anchor, title, body, metaLabel, preDone, canBack, isLast, onNext, onBack, onSkip, onRect,
}: {
  anchor?: string;
  title?: string;
  body: string;
  /** e.g. "Chapter 2/6 · Step 3 of 5" */
  metaLabel: string;
  /** The step's advance condition is already satisfied. */
  preDone: boolean;
  canBack: boolean;
  isLast: boolean;
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
  /** Reports the current hole rect (null when no anchor resolved). */
  onRect?: (r: Rect | null) => void;
}) {
  const [rect, setRect] = useState<Rect | null>(null);
  const scrolledFor = useRef<string | null>(null);
  // The card's own height, measured after every render so the placement below
  // can test whether it really fits rather than assuming a fixed 230px.
  const cardRef = useRef<HTMLDivElement>(null);
  const [cardH, setCardH] = useState(230);
  useLayoutEffect(() => {
    const h = cardRef.current?.offsetHeight;
    if (h && Math.abs(h - cardH) > 1) setCardH(h);
  });

  // Publish the hole so the mission panel can step out of its way.
  useEffect(() => {
    onRect?.(rect && { top: rect.top - 6, left: rect.left - 6, width: rect.width + 12, height: rect.height + 12 });
  }, [rect, onRect]);

  useEffect(() => {
    let disposed = false;
    const measure = () => {
      if (disposed) return;
      const el = anchor ? findAnchor(anchor) : null;
      if (el && scrolledFor.current !== anchor) {
        scrolledFor.current = anchor ?? null;
        try { el.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch { /* ignore */ }
      }
      const r = el ? el.getBoundingClientRect() : null;
      const next = r ? { top: r.top, left: r.left, width: r.width, height: r.height } : null;
      setRect((prev) => (sameRect(prev, next) ? prev : next));
    };
    measure();
    // Interval + resize/scroll listeners keep the hole glued to the target
    // while it moves (sticky bars, opening panels, window resizes).
    const iv = window.setInterval(measure, 200);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      disposed = true;
      window.clearInterval(iv);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [anchor]);

  // Card placement: below the hole, else above, else BESIDE it. The side
  // fallback exists because a tall anchor — the results modal the last step
  // annotates — leaves room neither above nor below, and the old
  // guessed-height version pushed the card clean off the top of the screen.
  // The real height is measured rather than assumed, so the fit test is exact.
  const CARD_W = Math.min(360, window.innerWidth - 24);
  let cardStyle: React.CSSProperties | undefined;
  if (rect) {
    const PAD = 14;
    const M = 12; // viewport margin
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const left = Math.max(M, Math.min(rect.left, vw - CARD_W - M));
    const below = rect.top + rect.height + PAD;
    const above = rect.top - PAD - cardH;
    if (below + cardH + M <= vh) {
      cardStyle = { top: below, left };
    } else if (above >= M) {
      cardStyle = { top: above, left };
    } else {
      const top = Math.max(M, Math.min(rect.top, vh - cardH - M));
      const roomRight = vw - (rect.left + rect.width) - M;
      cardStyle = roomRight >= rect.left - M
        ? { top, left: Math.min(rect.left + rect.width + PAD, vw - CARD_W - M) }
        : { top, left: Math.max(M, rect.left - PAD - CARD_W) };
    }
  }

  return (
    <>
      {rect ? (
        <div
          className="tut-hole"
          style={{ top: rect.top - 6, left: rect.left - 6, width: rect.width + 12, height: rect.height + 12 }}
        />
      ) : (
        <div className="tut-dim" />
      )}
      <div ref={cardRef} className={'tut-card' + (rect ? '' : ' tut-center')} style={cardStyle} role="dialog" aria-label={title || 'Tutorial step'}>
        <div className="mb-1 flex items-center justify-between gap-2 text-[11px] uppercase tracking-wide text-mut">
          <span>{metaLabel}</span>
          {preDone && <span className="text-good normal-case tracking-normal">✓ already done</span>}
        </div>
        {title && <div className="mb-1 text-[15px] font-extrabold text-txt">{title}</div>}
        <div className="text-sm leading-relaxed text-mut">{body}</div>
        <div className="mt-3 flex items-center gap-2">
          {canBack && (
            <button className="btn-ghost px-3 py-1.5 text-sm" onClick={onBack}>← Back</button>
          )}
          <button className="btn-primary px-3.5 py-1.5 text-sm" onClick={onNext}>
            {isLast ? 'Finish' : 'Next →'}
          </button>
          <button className="ml-auto text-xs text-dim hover:text-txt" onClick={onSkip}>
            Skip tour — I’ll explore
          </button>
        </div>
      </div>
    </>
  );
}
